import { advancedDefaults, filters, paletteValues } from './searchConfig';
import { ColorMode, Palette, SearchResult } from './types';

const isPalette = (value: string | null): value is Palette =>
  Boolean(value && paletteValues.includes(value as Palette));

export const getInitialPalette = (): Palette => {
  if (typeof window === 'undefined') {
    return 'blue';
  }

  const paletteParam = new URLSearchParams(window.location.search).get('theme');
  const savedPalette = window.localStorage.getItem('gotconn-palette');

  if (isPalette(paletteParam)) {
    return paletteParam;
  }

  return isPalette(savedPalette) ? savedPalette : 'blue';
};

export const getInitialColorMode = (): ColorMode => {
  if (typeof window === 'undefined') {
    return 'system';
  }

  const savedMode = window.localStorage.getItem('gotconn-color-mode');

  return savedMode === 'light' || savedMode === 'dark' || savedMode === 'system' ? savedMode : 'system';
};

export const getInitialQuery = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return new URLSearchParams(window.location.search).get('q') ?? '';
};

export const getInitialCategory = () => {
  if (typeof window === 'undefined') {
    return 'general';
  }

  const categoryParam = new URLSearchParams(window.location.search).get('category');

  return filters.some((filter) => filter.value === categoryParam) ? categoryParam as string : 'general';
};

export const getInitialAdvancedFilters = () => {
  if (typeof window === 'undefined') {
    return advancedDefaults;
  }

  const params = new URLSearchParams(window.location.search);

  return {
    language: params.get('language') || advancedDefaults.language,
    timeRange: params.get('time_range') || advancedDefaults.timeRange,
    safeSearch: params.get('safesearch') || advancedDefaults.safeSearch,
  };
};

export const getInitialPage = () => {
  if (typeof window === 'undefined') {
    return 1;
  }

  const pageParam = Number(new URLSearchParams(window.location.search).get('page') || '1');

  return Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
};

export const getResultHost = (url?: string) => {
  if (!url) {
    return '';
  }

  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

export const getDisplayText = (value: string | Record<string, unknown>) => {
  if (typeof value === 'string') {
    return value;
  }

  return [
    value.answer,
    value.title,
    value.content,
    value.url,
  ].find((candidate): candidate is string => typeof candidate === 'string' && candidate.trim().length > 0) || '';
};

export const getImageSource = (result: SearchResult) => result.img_src || result.thumbnail || result.thumbnail_src;

export const formatPublishedDate = (publishedDate?: string) => {
  if (!publishedDate) {
    return '';
  }

  const date = new Date(publishedDate);

  return Number.isNaN(date.getTime()) ? publishedDate : date.toLocaleDateString();
};

export const getResultBadges = (
  result: SearchResult,
  options: { includeCategory?: boolean; includeVideoMeta?: boolean } = {}
) => [
  result.engine,
  result.engines?.length ? result.engines.join(', ') : '',
  options.includeCategory ? result.category : '',
  options.includeVideoMeta ? result.length : '',
  formatPublishedDate(result.publishedDate),
].filter((badge): badge is string => Boolean(badge));
